import { useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import ScrollReveal from "@/components/base/ScrollReveal";
import { BLOG_HERO_IMAGE } from "@/data/blogAssets";
import { blogPosts } from "@/mocks/blogPosts";
import NotFound from "@/pages/NotFound";

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return <NotFound />;
  }

  const relatedPosts = blogPosts
    .filter((p) => p.slug !== slug && p.category === post.category)
    .slice(0, 2);

  const fallbackRelated = blogPosts
    .filter((p) => p.slug !== slug && !relatedPosts.find((r) => r.slug === p.slug))
    .slice(0, 2);

  const suggestions = relatedPosts.length > 0 ? relatedPosts : fallbackRelated;

  return (
    <>
      <title>{post.title} | Perspective Therapy Blog Larchmont NY</title>
      <meta name="description" content={post.excerpt} />
      <PageLayout>
        {/* Hero */}
        <section className="relative w-full min-h-[420px] md:min-h-[500px] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={BLOG_HERO_IMAGE}
              alt="Perspective Therapy blog"
              className="w-full h-full object-cover object-top"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(120deg, rgba(28,20,12,0.48) 0%, rgba(42,30,18,0.32) 50%, rgba(60,40,20,0.14) 100%)",
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to top, rgba(20,14,8,0.38) 0%, transparent 50%)",
              }}
            />
          </div>
          <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-36 pb-20">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-5">
                <span
                  className="font-manrope text-[11px] font-semibold px-3 py-1.5 rounded-full tracking-wide"
                  style={{ background: post.color + "20", color: post.color }}
                >
                  {post.category}
                </span>
                <span className="font-manrope text-white/60 text-[13px]">
                  {post.date} &middot; {post.readTime}
                </span>
              </div>
              <h1 className="font-fraunces text-white text-3xl md:text-4xl lg:text-[2.8rem] font-light leading-[1.2] mb-6">
                {post.title}
              </h1>
              <p className="font-manrope text-white/90 text-lg leading-relaxed max-w-xl">
                {post.excerpt}
              </p>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="w-full py-16 md:py-24" style={{ background: "linear-gradient(180deg, #F8F5EE 0%, #F5F1E8 45%, #F2EDE4 100%)" }}>
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-12 lg:gap-16">
              {/* Main content */}
              <ScrollReveal variant="fadeUp" delay={0}>
                <div className="bg-white/80 border rounded-2xl p-8 md:p-10 lg:p-12" style={{ borderColor: "rgba(203,191,175,0.30)" }}>
                  <div className="h-[3px] w-12 rounded-full mb-8" style={{ background: post.bar }} />
                  <article
                    className="blog-article prose prose-lg max-w-none font-manrope"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />

                  {/* Tags */}
                  <div className="mt-10 pt-8 border-t flex flex-wrap gap-2" style={{ borderColor: "rgba(203,191,175,0.30)" }}>
                    {post.keywords.map((kw) => (
                      <span
                        key={kw}
                        className="font-manrope text-[12px] font-medium px-3 py-1.5 rounded-full"
                        style={{ background: "rgba(43,43,43,0.06)", color: "rgba(43,43,43,0.7)" }}
                      >
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
              </ScrollReveal>

              {/* Sidebar */}
              <div className="flex flex-col gap-6">
                {/* CTA card */}
                <ScrollReveal variant="fadeUp" delay={80}>
                  <div className="bg-white/80 border rounded-2xl p-6" style={{ borderColor: "rgba(203,191,175,0.30)" }}>
                    <div className="flex items-center gap-2.5 mb-4">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: "rgba(60,111,248,0.09)" }}>
                        <i className="ri-calendar-check-line text-sm" style={{ color: "#3C6FF8" }}></i>
                      </div>
                      <h3 className="font-fraunces text-[#2B2B2B] text-lg font-medium">Ready to start therapy?</h3>
                    </div>
                    <p className="font-manrope text-[#2B2B2B]/78 text-[14px] leading-relaxed mb-5">
                      Book a free 15-minute consultation to ask questions and find the right therapist. No commitment required.
                    </p>
                    <a
                      href="/contact"
                      className="inline-flex items-center gap-2 text-white font-manrope font-semibold text-[14px] px-5 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap w-full justify-center"
                      style={{ background: "#3C6FF8" }}
                      onMouseEnter={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#2a5de6"}
                      onMouseLeave={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#3C6FF8"}
                    >
                      Book Consultation <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </ScrollReveal>

                {/* Services quick links */}
                <ScrollReveal variant="fadeUp" delay={160}>
                  <div className="bg-white/80 border rounded-2xl p-6" style={{ borderColor: "rgba(203,191,175,0.30)" }}>
                    <h3 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-4">Our Services</h3>
                    <div className="flex flex-col gap-2">
                      {[
                        { label: "Individual Therapy", href: "/individual-therapy", icon: "ri-user-line", color: "#3C6FF8" },
                        { label: "Couples Therapy", href: "/couples-therapy", icon: "ri-heart-3-line", color: "#6B742F" },
                        { label: "Teen Therapy", href: "/teen-therapy", icon: "ri-emotion-line", color: "#7D54FC" },
                      ].map((s) => (
                        <a
                          key={s.label}
                          href={s.href}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer group"
                          style={{ color: "#2B2B2B" }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.background = "rgba(60,111,248,0.06)";
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                          }}
                        >
                          <i className={`${s.icon} text-sm`} style={{ color: s.color }}></i>
                          <span className="font-manrope text-[14px] font-medium">{s.label}</span>
                          <i className="ri-arrow-right-line text-xs ml-auto opacity-40 group-hover:opacity-80 transition-opacity"></i>
                        </a>
                      ))}
                    </div>
                  </div>
                </ScrollReveal>

                {/* Original external link */}
                <ScrollReveal variant="fadeUp" delay={240}>
                  <div className="bg-white/80 border rounded-2xl p-6" style={{ borderColor: "rgba(203,191,175,0.30)" }}>
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: "rgba(107,116,47,0.09)" }}>
                        <i className="ri-external-link-line text-sm" style={{ color: "#6B742F" }}></i>
                      </div>
                      <h3 className="font-fraunces text-[#2B2B2B] text-lg font-medium">Full Blog</h3>
                    </div>
                    <p className="font-manrope text-[#2B2B2B]/78 text-[14px] leading-relaxed mb-4">
                      This article is also available on our full blog with additional resources and related reading.
                    </p>
                    <a
                      href={post.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="inline-flex items-center gap-1.5 font-manrope text-[14px] font-semibold transition-colors cursor-pointer whitespace-nowrap"
                      style={{ color: "#6B742F" }}
                    >
                      Read on Full Blog <i className="ri-arrow-right-up-line text-xs"></i>
                    </a>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {suggestions.length > 0 && (
          <section className="w-full py-16 md:py-20" style={{ background: "linear-gradient(180deg, #F2EEE4 0%, #EEE8DA 45%, #F5F1E8 100%)" }}>
            <div className="w-full px-6 md:px-12 lg:px-20">
              <ScrollReveal variant="fadeIn" delay={0}>
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-5 h-[2px] rounded-full" style={{ background: "linear-gradient(90deg, #6B742F, #B4A6E3)" }} />
                  <span className="font-manrope text-[13px] font-semibold tracking-widest uppercase" style={{ color: "#6B742F" }}>
                    Related Reading
                  </span>
                </div>
              </ScrollReveal>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-7">
                {suggestions.map((s, i) => (
                  <ScrollReveal key={s.slug} variant="fadeUp" delay={i * 100}>
                    <a
                      href={`/blog/${s.slug}`}
                      className="bg-white/80 border rounded-2xl overflow-hidden flex flex-col card-hover block"
                      style={{ borderColor: s.color + "22" }}
                    >
                      <div className="h-[3px] w-full shrink-0" style={{ background: s.bar }} />
                      <div className="p-6 md:p-7">
                        <div className="flex items-center gap-2 mb-3">
                          <span
                            className="font-manrope text-[11px] font-semibold px-2.5 py-1 rounded-full"
                            style={{ background: s.color + "12", color: s.color }}
                          >
                            {s.category}
                          </span>
                          <span className="font-manrope text-[12px] text-[#2B2B2B]/45">{s.readTime}</span>
                        </div>
                        <h3 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-2 leading-snug">
                          {s.title}
                        </h3>
                        <p className="font-manrope text-[#2B2B2B]/75 text-[14px] leading-relaxed mb-4">
                          {s.excerpt}
                        </p>
                        <span
                          className="inline-flex items-center gap-1.5 font-manrope text-[13px] font-semibold"
                          style={{ color: s.color }}
                        >
                          Read article <i className="ri-arrow-right-line text-xs"></i>
                        </span>
                      </div>
                    </a>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        <section className="w-full py-20 md:py-28" style={{ background: "linear-gradient(180deg, #EEE8DA 0%, #F5F1E8 45%, #F8F5EE 100%)" }}>
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-xl mx-auto text-center">
              <ScrollReveal variant="fadeUp" delay={0}>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: "rgba(60,111,248,0.09)" }}>
                    <i className="ri-mail-line text-sm" style={{ color: "#3C6FF8" }}></i>
                  </div>
                </div>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-4">
                  Want more insights delivered to your inbox?
                </h2>
                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed mb-8 max-w-md mx-auto">
                  Get blog notifications with practical mental health tips, new articles, and resources from Perspective Therapy.
                </p>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={80}>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/blog/all"
                    className="inline-flex items-center justify-center gap-2 text-white font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                    style={{ background: "#3C6FF8" }}
                    onMouseEnter={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#2a5de6"}
                    onMouseLeave={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#3C6FF8"}
                  >
                    Subscribe for Blog Updates <i className="ri-mail-send-line"></i>
                  </a>
                  <a
                    href="/contact"
                    className="inline-flex items-center justify-center gap-2 font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap border"
                    style={{ color: "#2B2B2B", borderColor: "rgba(43,43,43,0.18)" }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.background = "#2B2B2B";
                      (e.currentTarget as HTMLAnchorElement).style.color = "#fff";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                      (e.currentTarget as HTMLAnchorElement).style.color = "#2B2B2B";
                    }}
                  >
                    Book a Consultation
                  </a>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
}
